import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlarmClock, ArrowUpRight, CalendarPlus, Check, CircleAlert, ListFilter, Mail, MessageCircle, NotebookPen, Phone, RefreshCw, Video,
} from 'lucide-react';
import { toast } from 'react-toastify';

import { AuthContext } from 'src/context/AuthContext';
import axiosInstance from 'src/common/axiosInstance';
import StagePage from 'src/stage/StagePage';
import { WsDropdown, WsSegmented } from 'src/stage/WsControls';
import { Monogram, Skeleton, formatNumber } from 'src/stage/StageUi';
import { characterFor } from 'src/stage/characters';
import { accessFor } from 'src/stage/crmNav';
import { ContactButtons, HelpTip, SnoozeMenu } from './shared/StageKit';
import LogActivityDialog from './shared/LogActivityDialog';
import { ACTIVITY_TYPES, apiMessage, completeActivity, dueBucket, dueLabel, listFollowUps, rescheduleActivity } from './shared/crmWork';
import { fetchWorkspaceInsights } from './crmInsights';
import './home.css';
import './follow-ups.css';

const TYPE_ICONS = { CALL: Phone, WHATSAPP: MessageCircle, MEETING: Video, EMAIL: Mail, FOLLOWUP: AlarmClock, NOTE: NotebookPen };
const GROUPS = [
  { id: 'overdue', label: 'Overdue', hint: 'Promised and not done yet. Start here.' },
  { id: 'today', label: 'Today' },
  { id: 'tomorrow', label: 'Tomorrow' },
  { id: 'week', label: 'Later this week' },
  { id: 'later', label: 'Later' },
  { id: 'nodate', label: 'No date set' },
];
const TYPE_OPTIONS = [{ value: '', label: 'All kinds', short: 'All' }, ...['CALL', 'WHATSAPP', 'MEETING', 'EMAIL', 'FOLLOWUP'].map((value) => ({ value, label: ACTIVITY_TYPES[value].label }))];

function openLeadPath(lead) {
  const workspace = localStorage.getItem('autoappy_current_workspace_id') || '';
  if (lead?.stage?.pipeline_id) localStorage.setItem('selectedPipelineId', lead.stage.pipeline_id);
  return `/crm?lead=${lead?.id}&workspace=${encodeURIComponent(workspace)}`;
}

function FollowUpRow({ item, showOwner, busy, onDone, onSnooze, onOpen }) {
  const Icon = TYPE_ICONS[item.type] || AlarmClock;
  const late = dueBucket(item.scheduled_at) === 'overdue';
  const lead = item.lead || {};
  const what = `${ACTIVITY_TYPES[item.type]?.verb || 'Follow up with'} ${lead.name || 'lead'}`;
  return (
    <li className={`crm-fu-row${busy ? ' is-busy' : ''}`}>
      <span className={`crm-fu-when${late ? ' is-late' : ''}`}>{dueLabel(item.scheduled_at)}</span>
      <span className={`ws-icon-tile crm-home-tile${late ? ' ws-icon-tile--red' : ''}`} aria-hidden="true"><Icon size={15} /></span>
      <button type="button" className="crm-fu-main" onClick={onOpen}>
        <span className="crm-fu-title">{what}</span>
        <span className="crm-fu-sub">
          {lead.stage?.name && <span className="crm-fu-stage">{lead.stage.name}</span>}
          <span className="crm-fu-note">{item.note || (lead.phone ? lead.phone : 'No note')}</span>
        </span>
      </button>
      {showOwner && item.creator?.name && <span className="crm-fu-owner" title={`Planned by ${item.creator.name}`}><Monogram name={item.creator.name} size={24} /></span>}
      <span className="crm-fu-actions">
        <ContactButtons phone={lead.phone} name={lead.name} />
        <SnoozeMenu compact label="Snooze" disabled={busy} onPick={onSnooze} />
        <button type="button" className="ws-btn ws-btn--primary ws-btn--sm crm-fu-done" disabled={busy} onClick={onDone} aria-label={`Mark done: ${what}`}><Check size={13} />Done</button>
      </span>
    </li>
  );
}

export default function FollowUpsPage() {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const access = accessFor(currentUser);
  const canSeeEveryone = access.isAdmin || currentUser?.workspace_role === 'manager';
  const [scope, setScope] = useState('mine');
  const [type, setType] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [planFor, setPlanFor] = useState(null);
  const [idle, setIdle] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { items: rows, meta } = await listFollowUps({ mine: scope === 'mine', type });
      setItems(rows);
      setTotal(Number(meta.total ?? rows.length));
    } catch (loadError) {
      setError(apiMessage(loadError, 'We couldn’t load your follow-ups.'));
    } finally {
      setLoading(false);
    }
  }, [scope, type]);

  useEffect(() => { load(); }, [load]);

  // Open leads without any planned follow-up, across pipelines.
  const loadIdle = useCallback(async () => {
    try {
      const first = await axiosInstance.get('/analytics/crm-overview', { params: { days: 30, mode: 'queue' } });
      const pipelines = first.data?.data?.pipelines || [];
      const rows = await fetchWorkspaceInsights(pipelines, { days: 30, mode: 'queue' });
      const ids = rows.flatMap((row) => row.data?.queues?.without_next_activity?.lead_ids || []);
      const count = ids.length;
      if (!count) { setIdle({ count: 0, leads: [] }); return; }
      const leads = await axiosInstance.get('/leads', { params: { lead_ids: ids.slice(0, 6), per_page: 6 } });
      setIdle({ count, leads: leads.data?.data || [] });
    } catch {
      setIdle(null);
    }
  }, []);
  useEffect(() => { loadIdle(); }, [loadIdle]);

  const groups = useMemo(() => {
    const byGroup = Object.fromEntries(GROUPS.map((group) => [group.id, []]));
    items.forEach((item) => byGroup[dueBucket(item.scheduled_at)].push(item));
    return GROUPS.map((group) => ({ ...group, items: byGroup[group.id] })).filter((group) => group.items.length);
  }, [items]);
  const count = (id) => groups.find((group) => group.id === id)?.items.length || 0;
  const overdue = count('overdue');
  const today = count('today');
  const week = count('tomorrow') + count('week');

  const done = async (item) => {
    setBusyId(item.id);
    try {
      await completeActivity(item.id);
      setItems((rows) => rows.filter((row) => row.id !== item.id));
      setTotal((value) => Math.max(0, value - 1));
      window.dispatchEvent(new Event('crm:followups-changed'));
      toast.success(({ closeToast }) => (
        <span className="crm-toast">
          Done. What’s next for {String(item.lead?.name || 'this lead').split(/\s+/)[0]}?
          <button type="button" className="ws-btn ws-btn--plain" onClick={() => { closeToast(); setPlanFor(item.lead); }}>Plan follow-up</button>
        </span>
      ), { autoClose: 6000 });
    } catch (doneError) {
      toast.error(apiMessage(doneError, 'Couldn’t mark this as done.'));
    } finally {
      setBusyId(null);
    }
  };

  const snooze = async (item, when) => {
    setBusyId(item.id);
    try {
      await rescheduleActivity(item.id, when);
      setItems((rows) => rows.map((row) => (row.id === item.id ? { ...row, scheduled_at: when.toISOString() } : row))
        .sort((a, b) => (a.scheduled_at ? Date.parse(a.scheduled_at) : Infinity) - (b.scheduled_at ? Date.parse(b.scheduled_at) : Infinity)));
      window.dispatchEvent(new Event('crm:followups-changed'));
      toast.info(`Moved to ${dueLabel(when)}`);
    } catch (snoozeError) {
      toast.error(apiMessage(snoozeError, 'Couldn’t move this follow-up.'));
    } finally {
      setBusyId(null);
    }
  };

  const appy = characterFor('appy');
  const subtitle = loading ? 'Calls, meetings and messages you promised, in the order they’re due.'
    : overdue ? (today ? `${formatNumber(overdue)} overdue and ${formatNumber(today)} for today. Start with the overdue ones.` : `${formatNumber(overdue)} overdue. Clear those first; nothing else is due today.`)
      : today ? `${formatNumber(today)} to do today. Nothing is overdue.`
        : 'Nothing due today. Plan follow-ups for leads that don’t have one.';

  return (
    <StagePage
      className="crm-fu-page"
      eyebrow={<>Work<span className="crm-eyebrow-dot" />Follow-ups</>}
      title={<HelpTip term="followup">Follow-ups</HelpTip>}
      subtitle={subtitle}
      actions={(
        <>
          {canSeeEveryone && <WsSegmented label="Whose follow-ups" value={scope} onChange={setScope} items={[{ id: 'mine', label: 'Mine' }, { id: 'all', label: 'Everyone' }]} />}
          <WsDropdown label="Kind" icon={ListFilter} value={type} onChange={setType} options={TYPE_OPTIONS} align="right" />
          <button type="button" className="crm-icon-btn" onClick={() => { load(); loadIdle(); }} disabled={loading} aria-label="Refresh" title="Refresh"><RefreshCw size={16} className={loading ? 'ws-spin' : ''} /></button>
        </>
      )}
    >
      <section className="ws-card ws-kpis ws-rise" aria-label="Follow-up summary">
        <div className="ws-kpi"><span className="ws-kpi-label" style={overdue ? { color: '#AD3B3B' } : undefined}>{overdue ? <span className="ws-status ws-status--failed" aria-hidden="true" /> : null}Overdue</span><span className="ws-kpi-value">{loading ? '—' : formatNumber(overdue)}</span><span className="ws-muted">{overdue ? 'Do these first' : 'Nothing late'}</span></div>
        <div className="ws-kpi"><span className="ws-kpi-label">Today</span><span className="ws-kpi-value">{loading ? '—' : formatNumber(today)}</span><span className="ws-muted">Due before midnight</span></div>
        <div className="ws-kpi"><span className="ws-kpi-label">Next 7 days</span><span className="ws-kpi-value">{loading ? '—' : formatNumber(week)}</span><span className="ws-muted">{formatNumber(total)} planned in all</span></div>
        <div className="ws-kpi"><span className="ws-kpi-label">No next step</span><span className="ws-kpi-value">{idle ? formatNumber(idle.count) : '—'}</span><span className="ws-muted">Open leads nobody plans to contact</span></div>
      </section>

      {error && <div className="ws-notice" role="alert"><CircleAlert size={16} style={{ flex: 'none', marginTop: 1 }} /><span className="ws-grow">{error}</span><button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={load}>Try again</button></div>}

      <div className="crm-fu-layout">
        <section className="ws-card crm-fu-list ws-rise" aria-label="Follow-ups">
          {loading && !items.length ? (
            <div className="crm-fu-pad">{[0, 1, 2, 3, 4].map((index) => <div key={index} className="crm-fu-skel"><Skeleton width={90} /><Skeleton width={34} height={34} radius={11} /><div className="ws-grow"><Skeleton width="55%" /><Skeleton width="35%" height={10} style={{ marginTop: 6 }} /></div></div>)}</div>
          ) : !groups.length ? (
            <div className="crm-fu-empty">
              {appy?.full && <img src={appy.full} alt="" aria-hidden="true" />}
              <h2 className="ws-h2">You’re all caught up</h2>
              <p className="ws-muted">No follow-ups are planned{type ? ' of this kind' : ''}. {idle?.count ? `${formatNumber(idle.count)} open leads have no next step yet.` : 'New leads will show up here once you plan a call or meeting.'}</p>
              <Link className="ws-btn ws-btn--quiet" to="/crm">Open leads</Link>
            </div>
          ) : groups.map((group) => (
            <div key={group.id} className={`crm-fu-group is-${group.id}`}>
              <div className="crm-fu-group-head">
                <h2>{group.label}<span className="ws-num">{group.items.length}</span></h2>
                {group.hint && <span className="ws-muted">{group.hint}</span>}
              </div>
              <ul>
                {group.items.map((item) => (
                  <FollowUpRow key={item.id} item={item} showOwner={scope === 'all'} busy={busyId === item.id}
                    onDone={() => done(item)} onSnooze={(when) => snooze(item, when)} onOpen={() => navigate(openLeadPath(item.lead))} />
                ))}
              </ul>
            </div>
          ))}
          {!loading && total > items.length && <p className="ws-muted crm-fu-more">Showing the first {formatNumber(items.length)} of {formatNumber(total)}. Finish these and refresh to see more.</p>}
        </section>

        <aside className="crm-fu-aside">
          <section className="ws-card ws-card-pad ws-rise">
            <div className="ws-card-head"><h2 className="ws-h3 ws-grow">No next step yet</h2>{idle?.count > 6 && <span className="crm-count-pill ws-num">{formatNumber(idle.count)}</span>}</div>
            <p className="ws-muted">Open leads with nothing planned. Give each one a follow-up so it doesn’t go cold.</p>
            {!idle ? <Skeleton height={120} radius={12} style={{ marginTop: 10 }} /> : idle.leads.length === 0 ? (
              <div className="ws-empty crm-home-empty" style={{ minHeight: 110, marginTop: 10 }}><Check size={18} color="#1B7A5A" /><strong>Every open lead has a plan</strong></div>
            ) : (
              <ul className="crm-home-list">
                {idle.leads.map((lead) => (
                  <li key={lead.id} className="crm-home-person">
                    <Monogram name={lead.name} size={30} />
                    <span className="ws-grow"><span className="crm-home-row-title">{lead.name}</span><span className="crm-home-row-sub">{lead.stage?.name || 'No stage'}{lead.assigned_user?.name ? ` · ${lead.assigned_user.name}` : ' · No owner'}</span></span>
                    <button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={() => setPlanFor(lead)}><CalendarPlus size={13} />Plan</button>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="ws-card crm-fu-tip ws-rise">
            {appy?.image && <img src={appy.image} alt="" aria-hidden="true" />}
            <div>
              <h2 className="ws-h3">How to use this list</h2>
              <ol>
                <li>Clear <b>Overdue</b> first, then <b>Today</b>.</li>
                <li>Call or WhatsApp straight from the row.</li>
                <li>Press <b>Done</b>, then plan the next follow-up.</li>
                <li>Can’t do it now? <b>Snooze</b> it to a better time.</li>
              </ol>
              <Link to="/crm" className="crm-link">Go to leads<ArrowUpRight size={13} /></Link>
            </div>
          </section>
        </aside>
      </div>

      {planFor && <LogActivityDialog lead={planFor} mode="plan" onClose={() => setPlanFor(null)} onSaved={() => { load(); loadIdle(); }} />}
    </StagePage>
  );
}
